import React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  width: '100%',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 600,
  textAlign: 'center',
  marginBottom: 16,
  lineHeight: 1.3,
  [theme.breakpoints.down('sm')]: {
    fontSize: 20,
  },
}));

const OptionsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: 16,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const OptionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  flex: 1,
  padding: '20px 16px',
  borderRadius: 16,
  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid #e0e0e0',
  backgroundColor: '#fff',
  color: isSelected ? theme.palette.primary.main : theme.palette.grey[600],
  textTransform: 'none',
  fontSize: 14,
  lineHeight: 1.4,
  minHeight: 80,
  transition: 'border-color 120ms ease, color 120ms ease',
  '&:hover': {
    backgroundColor: '#fff',
    borderColor: isSelected ? theme.palette.primary.main : '#bdbdbd',
  },
}));

const Divider = styled('div')(({ theme }) => ({
  width: 2,
  backgroundColor: '#e0e0e0',
  alignSelf: 'stretch',
  margin: '10px 0',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: 2,
    margin: '0 0',
  },
}));

const Hint = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.grey[400],
  textAlign: 'center',
  marginTop: 8,
}));

export default function BinaryChoice({
  leftOption,
  rightOption,
  selected,
  onSelect,
  title,
  hint,
}) {
  const { t } = useTranslation();
  const heading = title ?? t('Assessment.binaryChoiceTitle');
  const help = hint ?? t('Assessment.binaryChoiceHint');
  return (
    <Container>
      <Title variant="h2">{heading}</Title>
      <OptionsContainer>
        <OptionButton
          variant="text"
          isSelected={selected === 'left'}
          onClick={() => onSelect('left')}
        >
          {leftOption}
        </OptionButton>
        <Divider />
        <OptionButton
          variant="text"
          isSelected={selected === 'right'}
          onClick={() => onSelect('right')}
        >
          {rightOption}
        </OptionButton>
      </OptionsContainer>
      <Hint>{help}</Hint>
    </Container>
  );
}
